'use client';

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  ArrowUp,
  ExternalLink,
  Share2,
  Pencil,
  Eye,
  MessageCircle,
  Calendar,
  Tag,
  Clock,
  Home,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';

import { Header } from '@/components/layout/header';
import { Footer } from '@/components/layout/footer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmbedFrame } from '@/components/page-builder/embed-frame';
import { cn } from '@/lib/utils';
import {
  getProductBySlugSafe,
  toggleProductVote,
  type ProductResponseApi,
  type GetProductResult,
  type MediaItemResponse,
} from '@/lib/services/productService';
import { ProductQASection } from '@/components/products/product-qa-section';
import { ProductComments } from '@/components/products/product-comments';
import { ProductTeamSection } from '@/components/products/product-team-section';
import { TeamManagementPanel } from '@/components/products/team-management-panel';
import { ProductStats } from '@/components/products/product-stats';
import { ProductEmbedWidget } from '@/components/products/product-embed-widget';
import { useAuth } from '@/lib/auth/auth-context';
import { useProductTracking } from '@/lib/analytics/use-product-tracking';

// ============================================================================
// STATUS TAG CONFIG
// ============================================================================

const STATUS_TAG_INFO: Record<string, { label: string; description: string; icon: string; color: string; bg: string }> = {
  investment: { label: 'Ищу инвестиции', description: 'Активно ищу инвесторов', icon: '💰', color: 'text-amber-600', bg: 'bg-amber-500/10' },
  feedback: { label: 'Хочу обратную связь', description: 'Нужны отзывы и предложения', icon: '🔥', color: 'text-rose-600', bg: 'bg-rose-500/10' },
  partnerships: { label: 'Открыт к партнерствам', description: 'Рассматриваю сотрудничество', icon: '🤝', color: 'text-blue-600', bg: 'bg-blue-500/10' },
  mentors: { label: 'Ищу менторов', description: 'Нужна помощь опытных предпринимателей', icon: '🎓', color: 'text-violet-600', bg: 'bg-violet-500/10' },
  hiring: { label: 'Нанимаем', description: 'Ищем специалистов в команду', icon: '👨‍💻', color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
};

// ============================================================================
// MODERATION MESSAGE
// ============================================================================

function ModerationMessage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">
            Продукт на модерации
          </h1>
          <p className="text-foreground-secondary mb-8">
            Продукт создан, но находится на модерации. А пока посмотрите другие продукты!
          </p>
          <Button asChild size="lg" className="gap-2">
            <Link href="/">
              <Home className="h-5 w-5" />
              На главную
            </Link>
          </Button>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

// ============================================================================
// NOT FOUND MESSAGE
// ============================================================================

function NotFoundMessage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">
            Продукт не найден
          </h1>
          <p className="text-foreground-secondary mb-8">
            Запрашиваемый продукт не существует или был удалён.
          </p>
          <Button asChild size="lg" className="gap-2">
            <Link href="/">
              <Home className="h-5 w-5" />
              На главную
            </Link>
          </Button>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

// ============================================================================
// LOADING STATE
// ============================================================================

function LoadingState() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
          {/* Hero skeleton */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Main hero content */}
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <Skeleton className="w-20 h-20 md:w-28 md:h-28 rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-4">
                  <Skeleton className="h-8 w-64" />
                  <Skeleton className="h-5 w-full max-w-md" />
                  {/* Status tags */}
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-32 rounded-lg" />
                    <Skeleton className="h-8 w-28 rounded-lg" />
                  </div>
                  {/* Categories */}
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-20 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                </div>
                {/* Vote button - desktop */}
                <Skeleton className="hidden md:block w-28 h-28 rounded-xl flex-shrink-0" />
              </div>
              {/* Mobile vote button */}
              <Skeleton className="md:hidden mt-6 h-14 w-full rounded-xl" />
            </div>
            {/* Stats bar */}
            <div className="px-6 md:px-8 py-4 bg-muted/40 border-t border-border flex items-center justify-between">
              <div className="flex items-center gap-6">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-12" />
                <Skeleton className="hidden sm:block h-5 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-28 rounded-md" />
                <Skeleton className="h-9 w-9 rounded-md" />
                <Skeleton className="h-9 w-9 rounded-md" />
              </div>
            </div>
            {/* Founder strip */}
            <div className="px-6 md:px-8 py-4 border-t border-border flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          </div>

          {/* Content skeleton (media) */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <Skeleton className="aspect-video w-full" />
            <div className="p-6 space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </div>

          {/* Q&A skeleton */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-9 w-32 rounded-md" />
            </div>
            <div className="p-6 space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comments skeleton */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <Skeleton className="h-6 w-32" />
            </div>
            <div className="p-6 space-y-6">
              {/* Comment input */}
              <div className="flex gap-3">
                <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                <Skeleton className="h-20 flex-1 rounded-lg" />
              </div>
              {/* Comments */}
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <div className="flex gap-4 pt-1">
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ============================================================================
// HERO SECTION
// ============================================================================

interface HeroSectionProps {
  product: ProductResponseApi;
  onVoteUpdate?: () => void;
  isOwner?: boolean;
  onManageTeam?: () => void;
  onCtaClick?: () => void;
}

function HeroSection({ product, onVoteUpdate, isOwner = false, onManageTeam, onCtaClick }: HeroSectionProps) {
  const [voted, setVoted] = useState(product.isVoted || false);
  const [voteCount, setVoteCount] = useState(product.voteCount);
  const [isVoting, setIsVoting] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleVote = async () => {
    if (!isAuthenticated) {
      toast.error("Войдите, чтобы голосовать");
      return;
    }

    if (isVoting) return;

    const previousVoted = voted;
    const previousCount = voteCount;
    const newVoted = !voted;

    // Optimistic update
    setVoted(newVoted);
    setVoteCount(newVoted ? previousCount + 1 : previousCount - 1);
    setIsVoting(true);

    try {
      const result = await toggleProductVote(product.slug);

      if (!result.success) {
        throw new Error(result.error?.message || "Не удалось проголосовать");
      }

      // Use backend response as source of truth
      if (result.data) {
        setVoteCount(result.data.voteCount);
        setVoted(result.data.isVoted);
      }

      // Success feedback
      if (newVoted) {
        toast.success(
            "Голос учтён! 👍"
        );
      } else {
        toast.success("Голос отменён");
      }

      onVoteUpdate?.();
    } catch (error) {
      // Rollback on error
      setVoted(previousVoted);
      setVoteCount(previousCount);

      console.error("Vote failed:", error);
      toast.error("Не удалось проголосовать. Попробуйте снова.");
    } finally {
      setIsVoting(false);
    }
  };
  const createdAt = new Date(product.createdAt);
  const launchDate = product.launchDate ? new Date(product.launchDate) : null;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Main Hero Content */}
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start gap-6 ">
          {/* Logo */}
          <div className="relative w-20 h-20 md:w-28 md:h-28 flex-shrink-0">
            {product.logoUrl ? (
              <Image
                src={product.logoUrl}
                alt={product.name}
                fill
                sizes="(max-width: 768px) 80px, 112px"
                priority
                className="object-contain rounded-xl"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary/80 rounded-xl">
                <span className="text-3xl md:text-4xl font-bold text-white">
                  {product.name?.charAt(0)?.toUpperCase() || 'P'}
                </span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {product.name}
              </h1>
              {launchDate && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-muted-foreground border border-border">
                  <Calendar className="h-3 w-3" />
                  {new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' }).format(launchDate)}
                </span>
              )}
            </div>

            {/* Tagline */}
            <p className="text-lg text-foreground-secondary mb-4">
              {product.tagline}
            </p>

            {/* Status Tags */}
            {product.statusTags && product.statusTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {product.statusTags.map((tag) => {
                  const info = STATUS_TAG_INFO[tag];
                  if (!info) return null;
                  return (
                    <span
                      key={tag}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                        info.bg,
                        info.color
                      )}
                      title={info.description}
                    >
                      <span>{info.icon}</span>
                      {info.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Categories */}
            {product.categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="h-4 w-4 text-foreground-tertiary" />
                {product.categories.map((cat) => (
                  <Badge key={cat.id} variant="secondary" className="text-xs">
                    {cat.name}
                  </Badge>
                ))}
              </div>
            )}

            {/* Collapsible Description */}
            {product.description && (
              <div className="mt-4">
                <button
                  onClick={() => setShowDescription(!showDescription)}
                  className="flex items-center gap-2 text-sm font-medium text-foreground-secondary hover:text-foreground transition-colors"
                >
                  <span>Подробнее о продукте</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform duration-200',
                      showDescription && 'rotate-180'
                    )}
                  />
                </button>
                {showDescription && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 text-base leading-relaxed text-foreground-secondary"
                  >
                    {product.description}
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Vote Button - Desktop */}
          <motion.button
            onClick={handleVote}
            disabled={isVoting}
            whileTap={{ scale: 0.97 }}
            className={cn(
              'hidden md:flex flex-col items-center justify-center gap-2 px-10 py-6 rounded-xl border-2 transition-colors',
              voted
                ? 'bg-primary border-primary text-primary-foreground'
                : 'bg-card border-border text-foreground hover:border-primary hover:bg-primary/5 hover:text-primary',
              isVoting && 'opacity-50 cursor-not-allowed'
            )}
          >
            <ArrowUp className={cn('h-7 w-7', voted && 'fill-current')} strokeWidth={2.5} />
            <span className="text-3xl font-bold tabular-nums">{voteCount}</span>
          </motion.button>
        </div>

        {/* Mobile Vote */}
        <motion.button
          onClick={handleVote}
          disabled={isVoting}
          whileTap={{ scale: 0.98 }}
          className={cn(
            'md:hidden mt-6 flex items-center justify-center gap-3 w-full px-6 py-4 rounded-xl border-2 transition-colors',
            voted
              ? 'bg-primary border-primary text-primary-foreground'
              : 'bg-card border-border text-foreground hover:border-primary hover:bg-primary/5 hover:text-primary',
            isVoting && 'opacity-50 cursor-not-allowed'
          )}
        >
          <ArrowUp className={cn('h-5 w-5', voted && 'fill-current')} strokeWidth={2.5} />
          <span className="text-lg font-bold">{voteCount} голосов</span>
        </motion.button>
      </div>

      {/* Stats & Actions Bar */}
      <div className="px-6 md:px-8 py-4 bg-muted/40 border-t border-border flex flex-wrap items-center justify-between gap-4">
        {/* Stats */}
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-foreground-secondary">
            <Eye className="h-4 w-4" />
            <span className="font-medium">{product.viewCount.toLocaleString('ru-RU')}</span>
          </div>
          <div className="flex items-center gap-2 text-foreground-secondary">
            <MessageCircle className="h-4 w-4" />
            <span className="font-medium">{product.commentCount}</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-foreground-tertiary">
            <Calendar className="h-4 w-4" />
            <span>{formatDistanceToNow(createdAt, { addSuffix: true, locale: ru })}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {product.websiteUrl && (
            <Button asChild size="sm" className="gap-2">
              <a
                href={product.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onCtaClick}
              >
                Открыть сайт
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
          {isOwner && (
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link href={`/products/${product.slug}/edit`}>
                <Pencil className="h-4 w-4" />
                Редактировать
              </Link>
            </Button>
          )}
          <Button variant="outline" size="icon" className="h-9 w-9">
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Team Section */}
      <ProductTeamSection
        owner={product.team?.owner ?? product.createdBy}
        members={product.team?.members ?? []}
        isOwner={isOwner}
        onManageTeam={onManageTeam}
      />
    </div>
  );
}

// ============================================================================
// MEDIA PREVIEW COMPONENTS
// ============================================================================

function ImagePreview({ item }: { item: Extract<MediaItemResponse, { type: 'image' }> }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative overflow-hidden">
      {!loaded && (
        <div className="aspect-video w-full bg-muted animate-pulse" />
      )}
      <img
        src={item.imageUrl}
        alt=""
        onLoad={() => setLoaded(true)}
        className={cn(
          'w-full h-auto',
          !loaded && 'absolute top-0 left-0 opacity-0 pointer-events-none'
        )}
      />
    </div>
  );
}

function VideoPreview({ item }: { item: Extract<MediaItemResponse, { type: 'video' }> }) {
  // Parse video URL to get embed URL
  const getEmbedUrl = (url: string): string | null => {
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0`;
    }
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return null;
  };

  const embedUrl = getEmbedUrl(item.url);
  if (!embedUrl) return null;

  return (
    <EmbedFrame
      src={embedUrl}
      scalePercent={item.scalePercent}
      offsetX={item.offsetX}
      offsetY={item.offsetY}
    />
  );
}

function IframePreview({ item }: { item: Extract<MediaItemResponse, { type: 'iframe' }> }) {
  return (
    <EmbedFrame
      src={item.src}
      aspectRatio={item.aspectRatio}
      scalePercent={item.scalePercent}
      offsetX={item.offsetX}
      offsetY={item.offsetY}
    />
  );
}

function TextPreview({ item }: { item: Extract<MediaItemResponse, { type: 'text' }> }) {
  return (
    <div className="px-6 py-4 text-base leading-relaxed text-foreground-secondary">
      {item.content}
    </div>
  );
}

function MediaBlock({ item }: { item: MediaItemResponse }) {
  switch (item.type) {
    case 'image':
      return <ImagePreview item={item} />;
    case 'video':
      return <VideoPreview item={item} />;
    case 'iframe':
      return <IframePreview item={item} />;
    case 'text':
      return <TextPreview item={item} />;
    case 'gallery':
      return <GalleryPreview item={item} />;
    default:
      return null;
  }
}

// ============================================================================
// CONTENT SECTION
// ============================================================================

function GalleryPreview({ item }: { item: Extract<MediaItemResponse, { type: 'gallery' }> }) {
  const count = item.images.length;
  const cols = count <= 2 ? 'grid-cols-2' : count === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4';

  return (
    <div className={cn('grid gap-0', cols)}>
      {item.images.map((image, index) => (
        <img
          key={index}
          src={image.imageUrl}
          alt=""
          className="w-full h-auto"
        />
      ))}
    </div>
  );
}

function ContentSection({ product }: { product: ProductResponseApi }) {
  const hasMedia = product.media && product.media.length > 0;
  const hasHighlights = product.highlights && product.highlights.length > 0;

  // Don't render anything if no content
  if (!hasMedia && !hasHighlights) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Media */}
      {hasMedia && (
        <div>
          {product.media!.map((item, index) => (
            <div key={index}>
              <MediaBlock item={item} />
            </div>
          ))}
        </div>
      )}

      {/* Highlights */}
      {hasHighlights && (
        <div className="px-6 py-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Ключевые особенности</h3>
          <ul className="space-y-2">
            {product.highlights!.map((highlight, index) => (
              <li key={index} className="flex items-start gap-2 text-foreground-secondary">
                <span className="text-primary mt-1">•</span>
                {highlight}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function ProductDetailClient({ initialResult }: { initialResult?: GetProductResult }) {
  const params = useParams();
  const slug = params.slug as string;
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const [result, setResult] = useState<GetProductResult | null>(initialResult ?? null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTeamManagement, setShowTeamManagement] = useState(false);

  useEffect(() => {
    // Wait until auth resolves before deciding whether a personalized refetch
    // is actually needed.
    if (isAuthLoading) return;

    let cancelled = false;
    async function loadProduct() {
      // Confirmed-anonymous visitor with a viewable server-rendered payload: the
      // unauthenticated server fetch already has everything (isVoted is false
      // for guests, no owner-only access to pending products), so skip the
      // redundant client request and use the seeded result directly.
      if (!isAuthenticated && initialResult?.status === 'success') {
        if (!cancelled) {
          setResult(initialResult);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      if (!slug) {
        if (!cancelled) {
          setResult({ status: 'not_found' });
          setIsLoading(false);
        }
        return;
      }
      // Revalidate through the authenticated proxy so personalized fields
      // (isVoted, owner access to pending products) are correct.
      const res = await getProductBySlugSafe(slug);
      if (cancelled) return;
      setResult(res);
      setIsLoading(false);
    }
    loadProduct();
    return () => {
      cancelled = true;
    };
  }, [slug, isAuthLoading, isAuthenticated, initialResult]);

  // Handle hash scroll after content loads
  useEffect(() => {
    if (!isLoading && result?.status === 'success') {
      const hash = window.location.hash;
      if (hash) {
        // Small delay to ensure DOM is rendered
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  }, [isLoading, result]);

  // Engagement tracking (SDD_PRODUCT_ANALYTICS.md): runs for ALL visitors, not
  // owner-gated; owner/team sessions are tagged self_view instead of dropped.
  const contentEndRef = useRef<HTMLDivElement>(null);
  const loadedProduct = result?.status === 'success' ? result.data : null;
  const selfView = !!(
    isAuthenticated &&
    loadedProduct &&
    (user?.id === loadedProduct.createdBy.id ||
      loadedProduct.team?.members?.some((m) => m.userId === user?.id))
  );
  const trackCtaClick = useProductTracking(slug, {
    contentEndRef,
    selfView,
    enabled: !isLoading && !!loadedProduct,
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (!result) {
    return <NotFoundMessage />;
  }

  if (result.status === 'forbidden') {
    return <ModerationMessage />;
  }

  if (result.status === 'not_found') {
    return <NotFoundMessage />;
  }

  if (result.status === 'error') {
    return <NotFoundMessage />;
  }

  const product = result.data;
  const isOwner = isAuthenticated && user?.id === product.createdBy.id;
  const isAdmin = isAuthenticated && user?.roles?.includes('admin');
  const isTeamMember = isAuthenticated && product.team?.members?.some(m => m.userId === user?.id);
  const canViewStats = isOwner || isAdmin || isTeamMember;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
          {!product.approved && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <Clock className="h-5 w-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Продукт находится на модерации. После проверки он станет доступен всем пользователям.
              </p>
            </div>
          )}
          <motion.div key="hero" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <HeroSection
              product={product}
              isOwner={isOwner}
              onManageTeam={() => setShowTeamManagement(true)}
              onCtaClick={() => trackCtaClick('hero')}
            />
          </motion.div>

          {canViewStats && (
            <motion.div key="stats" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <ProductStats productSlug={product.slug} />
            </motion.div>
          )}

          <motion.div key="content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <ContentSection product={product} />
          </motion.div>

          {canViewStats && (
            <motion.div key="embed-widget" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <ProductEmbedWidget slug={product.slug} productName={product.name} />
            </motion.div>
          )}

          <motion.div key="qa" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <ProductQASection
              productSlug={product.slug}
              ownerId={product.createdBy.id}
              founderName={product.createdBy.name}
              founderAvatar={product.createdBy.avatarUrl}
            />
          </motion.div>

          {/* Scroll-depth boundary: content above, comments/footer excluded (§3.2) */}
          <div ref={contentEndRef} aria-hidden="true" />

          <motion.div
            key="comments"
            id="comments"
            className="scroll-mt-6 -mx-4 md:mx-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <ProductComments productSlug={product.slug} ownerId={product.createdBy.id} />
          </motion.div>
        </div>
      </main>
      <Footer />

      {/* Team Management Panel (Owner Only) */}
      {isOwner && (
        <TeamManagementPanel
          open={showTeamManagement}
          onOpenChange={setShowTeamManagement}
          productSlug={product.slug}
          owner={product.team?.owner ?? product.createdBy}
          members={product.team?.members ?? []}
        />
      )}
    </div>
  );
}
